/**
 * @file ThemeContext.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Theme context provider for the Shay Suite Admin application.
 *              This file manages application theming, including light/dark mode support,
 *              system preference detection, and Material-UI theme customization.
 *              It provides a consistent theme across the entire application.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

/**
 * Type definition for theme modes
 * Supports system preference, light mode, and dark mode
 */
type ThemeMode = 'system' | 'light' | 'dark';

/**
 * Interface defining the structure of the theme context
 * Provides theme state and methods for theme management
 */
interface ThemeContextType {
  themeMode: ThemeMode;                    // Current theme mode setting
  setThemeMode: (mode: ThemeMode) => void; // Function to change theme mode
  isDarkMode: boolean;                     // Whether dark mode is currently active
}

/**
 * React context for theme state management
 * Provides theme data and methods to child components
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Custom hook to access theme context
 * 
 * @returns {ThemeContextType} The theme context value
 * @throws {Error} If used outside of ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Props interface for the ThemeProvider component
 */
interface ThemeProviderProps {
  children: ReactNode;  // Child components to be wrapped by the provider
}

/**
 * Theme provider component that manages application theming
 * 
 * This component:
 * - Manages theme mode state (system, light, dark)
 * - Detects system color scheme preference
 * - Creates and provides Material-UI theme
 * - Handles theme switching functionality
 * - Provides consistent styling across the application
 * 
 * @param {ThemeProviderProps} props - Component props containing children
 * @returns {JSX.Element} Context provider wrapping children with theme
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme mode state
  const [themeMode, setThemeMode] = useState<ThemeMode>('system');
  
  /**
   * Determines if the system prefers dark mode
   * Checks the user's system color scheme preference
   * 
   * @returns {boolean} True if system prefers dark mode, false otherwise
   */
  const getSystemDarkMode = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  // Determine current dark mode state based on theme mode and system preference
  const isDarkMode = themeMode === 'system' ? getSystemDarkMode() : themeMode === 'dark';

  /**
   * Creates the Material-UI theme with custom styling
   * Includes palette configuration and component style overrides
   */
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#1cabde',  // Primary brand color
      },
      secondary: {
        main: '#f48fb1',  // Secondary accent color
      },
      background: {
        default: isDarkMode ? '#121212' : '#f5f5f5',  // Background colors
        paper: isDarkMode ? '#1e1e1e' : '#ffffff',     // Paper/surface colors
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: '#1cabde',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1899c7',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
              color: '#666666',
            },
          },
          outlinedPrimary: {
            borderColor: '#1cabde',
            color: '#1cabde',
            '&:hover': {
              backgroundColor: 'rgba(28, 171, 222, 0.08)',
            },
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, isDarkMode }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 